// src/api/admin/adminListUsers.js
import { api } from "../client";
import { API_BASE_URL } from "../../utils/constants";

const LIST_METHOD = `${API_BASE_URL}/api/method/dynesourcerapi.user_directory.admin_list_users`;
const DELETE_METHOD = `${API_BASE_URL}/api/method/dynesourcerapi.user_directory.admin_delete_user`;

/**
 * List users by role (e.g. all lenders). Used by Admin Lender Management.
 * GET .../admin_list_users?limit_start=0&limit_page_length=20&role=Lender&exclude_system_manager=1&search=...
 *
 * @param {Object} params - Query parameters
 * @param {number} [params.limit_start=0] - Pagination offset
 * @param {number} [params.limit_page_length=20] - Page size
 * @param {string} [params.role=Lender] - Role filter (e.g. "Lender")
 * @param {number} [params.exclude_system_manager=1] - Exclude system users
 * @param {string} [params.search] - Search term (e.g. name, email)
 * @returns {Promise<{ data: Array, total?: number }>} List of user/lender records
 */
export async function adminListUsers(params = {}) {
  const qParams = {
    limit_start: 0,
    limit_page_length: 20,
    role: "Lender",
    exclude_system_manager: 1,
    ...params,
  };

  const response = await api.get(LIST_METHOD, { params: qParams });
  const raw = response?.data?.message ?? response?.data;

  if (Array.isArray(raw)) {
    return { data: raw, total: raw.length };
  }
  if (raw && Array.isArray(raw.data)) {
    const total = raw.total_count ?? raw.total ?? raw.data.length;
    return { data: raw.data, total };
  }
  if (raw && typeof raw === "object" && "data" in raw) {
    const data = raw.data;
    const total = raw.total_count ?? raw.total ?? 0;
    return { data: Array.isArray(data) ? data : [], total };
  }
  return { data: [], total: 0 };
}

/**
 * Map of lender email (lower-case) → display name, across every page of lenders.
 * @returns {Promise<Map<string, string>>}
 */
export async function fetchLenderNameByEmail() {
  const byEmail = new Map();
  const pageSize = 200;
  for (let start = 0; start < 100000; start += pageSize) {
    const { data, total } = await adminListUsers({ limit_start: start, limit_page_length: pageSize });
    data.forEach((u) => {
      const email = String(u?.email || u?.name || "").trim().toLowerCase();
      const display = String(
        u?.company_name ||
          u?.full_name ||
          [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
          ""
      ).trim();
      if (email && display) byEmail.set(email, display);
    });
    if (data.length < pageSize || (total && start + pageSize >= total)) break;
  }
  return byEmail;
}

/**
 * Delete a user (borrower or lender). Used by Admin Lender and Borrower Management.
 * POST .../admin_delete_user with body { user: "email@example.com" }
 *
 * @param {string} user - User name/email (Frappe User doc name)
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function adminDeleteUser(user) {
  if (!user || typeof user !== "string" || !user.trim()) {
    return { ok: false, error: "User is required" };
  }
  const userName = user.trim();
  try {
    await api.post(DELETE_METHOD, { user: userName });
    return { ok: true };
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.exc ||
      err?.message ||
      "Failed to delete user";
    return { ok: false, error: typeof message === "string" ? message : "Failed to delete user" };
  }
}

export default adminListUsers;
